import React, { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { 
  CheckCircle2, Clock, XCircle, Copy, Check, 
  ExternalLink, ChevronDown, ChevronUp, ShieldCheck, 
  Sparkles, RefreshCw, AlertCircle, ArrowLeft,
  Info, Mail, CreditCard, Send
} from 'lucide-react';
import { auth, db } from '../utils/firebase';
import { submitPayPalPayment, PAYPAL_EMAIL } from '../services/upiService';

const AMOUNTS = { monthly: 0.99, annual: 2.99, lifetime: 7.99 };
const PLAN_LABELS = { monthly: 'Monthly Pass', annual: 'Annual Pass', lifetime: 'Lifetime Access' };
const PENDING_STORAGE_KEY = 'webiptv_pending_paypal';

export default function PayPalDirectCheckout({ planId = 'monthly', onVerified }) {
  const [transactionId, setTransactionId] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const amount = AMOUNTS[planId] || 0.99;
  const planLabel = PLAN_LABELS[planId] || 'Premium Pass';

  // Restore existing pending payment from storage if exists for current user
  useEffect(() => {
    try {
      const saved = localStorage.getItem(PENDING_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.paymentId && parsed?.planId === planId) {
          setReceipt(parsed);
        }
      }
    } catch (e) {}
  }, [planId]);

  // Pre-fill user email as default sender email
  useEffect(() => {
    if (auth.currentUser?.email && !senderEmail) {
      setSenderEmail(auth.currentUser.email);
    }
  }, [senderEmail]);

  // Real-time onSnapshot listener: instantly auto-activates when admin approves!
  useEffect(() => {
    if (!receipt?.paymentId) return;

    const paymentRef = doc(db, 'upiPayments', receipt.paymentId);
    const unsubscribe = onSnapshot(paymentRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        if (data.status && data.status !== receipt.status) {
          setReceipt(prev => ({ ...prev, ...data }));
          if (data.status === 'approved') {
            localStorage.removeItem(PENDING_STORAGE_KEY);
            if (onVerified) onVerified(planId);
          }
        }
      }
    }, (err) => {
      console.warn('Real-time payment subscription error:', err);
    });

    return () => unsubscribe();
  }, [receipt?.paymentId, receipt?.status, onVerified, planId]);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(PAYPAL_EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // fallback
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError('');

    const cleanTx = transactionId.trim();
    if (!cleanTx || cleanTx.length < 5) {
      setError('Please enter a valid PayPal Transaction ID (e.g. 8XY12345AB678901C).');
      return;
    }

    setBusy(true);
    try {
      const result = await submitPayPalPayment(planId, cleanTx, senderEmail);
      const newReceipt = {
        paymentId: result.paymentId,
        transactionId: cleanTx,
        senderEmail: senderEmail.trim(),
        planId,
        amount,
        currency: 'USD',
        status: result.status || 'pending',
        submittedAt: new Date().toISOString()
      };
      setReceipt(newReceipt);
      try {
        localStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(newReceipt));
      } catch (err) {}
      if (result.status === 'approved' && onVerified) {
        onVerified(planId);
      }
    } catch (err) {
      setError(err.message || 'Could not submit PayPal reference. Please check connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleReset = () => {
    localStorage.removeItem(PENDING_STORAGE_KEY);
    setReceipt(null);
    setTransactionId('');
    setError('');
  };

  // Direct PayPal Web Transfer Link
  const paypalSendUrl = `https://www.paypal.com/myaccount/transfer/homepage/buy/preview?recipient=${encodeURIComponent(PAYPAL_EMAIL)}&amount=${amount.toFixed(2)}&currencyCode=USD`;

  return (
    <div className="w-full space-y-4">
      {/* Top Banner */}
      <div className="flex items-center justify-between p-3 rounded-2xl bg-gradient-to-r from-sky-500/10 via-blue-500/10 to-indigo-500/10 border border-sky-400/20">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-sky-500/20 border border-sky-400/30 flex items-center justify-center text-sky-400 font-bold text-xs">
            P
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-extrabold text-white text-xs sm:text-sm">Direct PayPal Transfer</span>
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                0% Extra Fees
              </span>
            </div>
            <p className="text-[11px] text-slate-400">Zero gateway errors • Send to verified PayPal account</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm sm:text-base font-extrabold text-white">${amount.toFixed(2)}</div>
          <div className="text-[10px] text-slate-400">USD one-time</div>
        </div>
      </div>

      {receipt ? (
        /* Status Card */
        <div className="p-4 sm:p-5 rounded-2xl bg-slate-900/90 border border-white/10 space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              {receipt.status === 'approved' ? (
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 flex items-center justify-center">
                  <CheckCircle2 size={18} />
                </div>
              ) : receipt.status === 'rejected' ? (
                <div className="w-8 h-8 rounded-full bg-rose-500/20 border border-rose-500/40 text-rose-400 flex items-center justify-center">
                  <XCircle size={18} />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-400 flex items-center justify-center animate-pulse">
                  <Clock size={18} />
                </div>
              )}
              <div>
                <h4 className="text-xs sm:text-sm font-bold text-white">
                  {receipt.status === 'approved' 
                    ? 'Payment Verified! Pro Unlocked'
                    : receipt.status === 'rejected'
                      ? 'Verification Needs Review'
                      : 'Payment Verification In Progress'}
                </h4>
                <p className="text-[11px] text-slate-400">
                  {receipt.status === 'approved'
                    ? 'Thank you! Your account has been upgraded to Premium.'
                    : receipt.status === 'rejected'
                      ? (receipt.rejectionReason || 'The reference could not be matched. Please contact support.')
                      : 'Listening for real-time approval from admin (Est. 5-15 mins).'}
                </p>
              </div>
            </div>

            {receipt.status === 'pending' && (
              <span className="flex items-center gap-1 text-[10px] font-semibold text-amber-300 bg-amber-400/10 px-2 py-0.5 rounded-full border border-amber-400/20">
                <RefreshCw size={10} className="animate-spin" /> Live Sync
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs bg-slate-950/60 p-3 rounded-xl border border-white/5">
            <div>
              <span className="text-slate-400 block text-[10px]">Plan</span>
              <span className="font-semibold text-white">{planLabel}</span>
            </div>
            <div>
              <span className="text-slate-400 block text-[10px]">Amount</span>
              <span className="font-semibold text-emerald-400">${amount.toFixed(2)} USD</span>
            </div>
            <div className="col-span-2 pt-1 border-t border-white/5">
              <span className="text-slate-400 block text-[10px]">PayPal Transaction ID</span>
              <span className="font-mono text-xs text-sky-300 break-all">{receipt.transactionId}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={handleReset}
              className="text-xs text-slate-400 hover:text-white underline transition"
            >
              Submit different Transaction ID
            </button>
          </div>
        </div>
      ) : (
        /* Action Form */
        <div className="space-y-4">
          {/* Step 1: Send Money to PayPal */}
          <div className="p-4 rounded-2xl bg-slate-900/80 border border-white/10 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-extrabold uppercase tracking-wider text-sky-400 flex items-center gap-1.5">
                <span className="w-4 h-4 rounded-full bg-sky-500/20 flex items-center justify-center text-[10px] text-sky-300 font-mono">1</span>
                Send to PayPal Account
              </span>
              <span className="text-xs font-bold text-emerald-400">${amount.toFixed(2)} USD</span>
            </div>

            {/* PayPal Recipient Pill */}
            <div className="flex items-center justify-between gap-2 p-2.5 rounded-xl bg-slate-950/80 border border-sky-500/30">
              <div className="flex items-center gap-2 min-w-0">
                <Mail size={16} className="text-sky-400 shrink-0" />
                <div className="truncate">
                  <div className="text-[10px] text-slate-400">PayPal Email / ID</div>
                  <div className="font-mono text-xs sm:text-sm font-bold text-white truncate">{PAYPAL_EMAIL}</div>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCopyEmail}
                className="shrink-0 px-3 py-1.5 rounded-lg bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 text-xs font-semibold flex items-center gap-1 transition border border-sky-400/30"
              >
                {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                <span>{copied ? 'Copied' : 'Copy'}</span>
              </button>
            </div>

            {/* Quick 1-Click Launch Button */}
            <a
              href={paypalSendUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-2.5 px-4 rounded-xl bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white font-bold text-xs flex items-center justify-center gap-2 transition shadow-lg shadow-blue-500/20"
            >
              <Send size={14} />
              <span>Open PayPal & Send ${amount.toFixed(2)} USD</span>
              <ExternalLink size={12} className="opacity-70" />
            </a>
          </div>

          {/* Step 2: Enter Transaction ID */}
          <form onSubmit={handleSubmit} className="p-4 rounded-2xl bg-slate-900/80 border border-white/10 space-y-3">
            <span className="text-[11px] font-extrabold uppercase tracking-wider text-sky-400 flex items-center gap-1.5">
              <span className="w-4 h-4 rounded-full bg-sky-500/20 flex items-center justify-center text-[10px] text-sky-300 font-mono">2</span>
              Enter Transaction Reference
            </span>

            <div className="space-y-2">
              <div>
                <label className="block text-[10px] text-slate-400 font-semibold mb-1">
                  PayPal Transaction ID <span className="text-rose-400">*</span>
                </label>
                <input
                  type="text"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  placeholder="e.g. 8XY12345AB678901C"
                  className="w-full px-3 py-2.5 rounded-xl bg-slate-950/80 border border-white/10 text-white font-mono text-xs focus:outline-none focus:border-sky-400 transition"
                  required
                />
              </div>

              <div>
                <label className="block text-[10px] text-slate-400 font-semibold mb-1">
                  Your PayPal Email <span className="text-slate-500">(Optional)</span>
                </label>
                <input
                  type="email"
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                  placeholder="The email associated with your PayPal account"
                  className="w-full px-3 py-2 rounded-xl bg-slate-950/80 border border-white/10 text-white text-xs focus:outline-none focus:border-sky-400 transition"
                />
              </div>
            </div>

            {error && (
              <div className="p-2.5 rounded-xl bg-rose-500/15 border border-rose-500/30 text-rose-300 text-xs flex items-center gap-2">
                <AlertCircle size={14} className="shrink-0 text-rose-400" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={busy || !transactionId.trim()}
              className="w-full py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-950 font-extrabold text-xs flex items-center justify-center gap-2 transition shadow-lg shadow-emerald-500/20"
            >
              {busy ? (
                <>
                  <RefreshCw size={14} className="animate-spin" />
                  <span>Verifying...</span>
                </>
              ) : (
                <>
                  <Sparkles size={14} />
                  <span>Verify & Activate Access</span>
                </>
              )}
            </button>

            {/* Instruction toggle */}
            <div className="pt-1">
              <button
                type="button"
                onClick={() => setShowGuide(!showGuide)}
                className="w-full flex items-center justify-between text-[11px] text-slate-400 hover:text-slate-300 py-1 transition"
              >
                <span className="flex items-center gap-1.5">
                  <Info size={12} className="text-sky-400" />
                  How does direct PayPal activation work?
                </span>
                {showGuide ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              </button>
              {showGuide && (
                <div className="p-3 rounded-xl bg-slate-950/60 border border-white/5 text-[11px] text-slate-300 space-y-1.5 mt-1 leading-relaxed">
                  <p>1. Send <strong>${amount.toFixed(2)} USD</strong> directly to <strong>{PAYPAL_EMAIL}</strong> using PayPal.</p>
                  <p>2. Open your PayPal receipt or email to find your 17-character alphanumeric <strong>Transaction ID</strong>.</p>
                  <p>3. Submit the ID here. Our system records your claim and notifies the administrator. Your account is activated instantly once verified!</p>
                </div>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
